/**
 * Reusable client-side sorters for Ant Design Table (data stays fully client-side).
 */

/** Natural / locale-aware string compare on a top-level field. */
export function sortByField(field) {
    return (a, b) =>
        String(a[field] ?? '').localeCompare(String(b[field] ?? ''), undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * Natural compare for control IDs of any shape: 5.2 < 5.10, "Art. 2" < "Art. 10", A.1.2.2 < A.1.2.10,
 * AC-2 < AC-2(1) < AC-10, GV.OC-01 < GV.OC-02, §4(1) < §4(2) < §10. Digit runs compare as numbers,
 * everything else case-insensitively; a shorter ID that prefixes a longer one sorts first.
 */
export function naturalCompare(x, y) {
    const tok = (v) => String(v ?? '').toLowerCase().match(/\d+|\D+/g) || [];
    const a = tok(x);
    const b = tok(y);
    const n = Math.min(a.length, b.length);
    for (let i = 0; i < n; i++) {
        const ad = /^\d/.test(a[i]);
        const bd = /^\d/.test(b[i]);
        if (ad && bd) {
            const d = parseInt(a[i], 10) - parseInt(b[i], 10);
            if (d !== 0) return d;
        } else if (a[i] !== b[i]) {
            return a[i] < b[i] ? -1 : 1;
        }
    }
    return a.length - b.length;
}

/** Control ID column sorter (ISO Annex A, PCI, NIST, GDPR articles, FedRAMP, DPDPA sections …). */
export function sortAnnexControlNumber(a, b) {
    return naturalCompare(a.ctrlNumber, b.ctrlNumber);
}

export function sortKeyPointsCount(a, b) {
    return (a.keyPoints?.length || 0) - (b.keyPoints?.length || 0);
}

/** Mapping cells that use "N/A" or empty — sort blanks last. */
export function sortTaxonomyMappingField(field) {
    return (a, b) => {
        const va = a[field];
        const vb = b[field];
        const emptyA = va == null || va === '' || va === 'N/A';
        const emptyB = vb == null || vb === '' || vb === 'N/A';
        if (emptyA && emptyB) return 0;
        if (emptyA) return 1;
        if (emptyB) return -1;
        return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    };
}

export function sortStatusField(field) {
    return (a, b) =>
        String(a[field] ?? '').localeCompare(String(b[field] ?? ''), undefined, { sensitivity: 'base' });
}
